// The boat's cameras on the web (TOM-797, cloud ADR 0411): the wire shapes of
// the camera reads (api_contracts.md §5.24) and the live player's contract,
// and the words every camera surface says. Pure, so the page, the alarm
// screens and their tests read one source.
//
// The event list is always "Seen by Neomaris since connected": what core heard
// while it was connected to the camera system, never that system's history.

import { NIGHT_TINT } from '@neomaris/controls';
import type { CSSProperties } from 'react';

export type CameraQuality = 'high' | 'medium' | 'low';

/** The shared streams, best first. */
export const CAMERA_QUALITIES: readonly CameraQuality[] = ['high', 'medium', 'low'];

export interface CameraSnapshotRef {
  snapshotId: string;
  takenAt: string;
  width: number;
  height: number;
}

export interface CameraStream {
  quality: CameraQuality;
  /** Each null when the boat could not read it. */
  bitrateBps: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
}

export interface Camera {
  cameraId: string;
  name: string;
  model?: string | null;
  state: string;
  streams: CameraStream[];
  online: boolean;
  latestSnapshot: CameraSnapshotRef | null;
  lastPersonAt: string | null;
  recentPerson: boolean;
  /** A pan, tilt and zoom camera's presets and patrols; a list is null when unknown (TOM-831). */
  ptz?: CameraPtz;
}

export interface CameraPtzSlot {
  slot: number;
  name: string;
}

export interface CameraPtz {
  presets: CameraPtzSlot[] | null;
  patrols: CameraPtzSlot[] | null;
  activePatrolSlot: number | null;
}

export interface CamerasResponse {
  nvr: { name: string | null; version: string | null } | null;
  observedAt: string | null;
  cameras: Camera[];
  eventsLabel: string;
  /** Whether "Take a picture now" works on this boat's core (TOM-838). Absent on an older cloud. */
  snapshotRequests?: { available: boolean; minCoreVersion: string };
  /** Whether this boat's core moves its pan, tilt and zoom cameras (TOM-831). */
  ptz?: { available: boolean; minCoreVersion: string };
}

export type CameraEventType = 'person' | 'smart' | 'audio' | 'motion' | 'offline' | 'gap';

export interface CameraEvent {
  eventId: string;
  /** Null only on a gap covering every camera. */
  cameraId: string | null;
  cameraName: string | null;
  type: CameraEventType;
  smartDetectTypes: string[];
  audioKind: string | null;
  start: string;
  end: string | null;
  snapshot: CameraSnapshotRef | null;
}

export interface CameraEventsResponse {
  label: string;
  events: CameraEvent[];
  nextBefore: string | null;
}

/** POST …/cameras/:cameraId/live → a session (the live contract, TOM-797). */
/**
 * The video the player plays: the relay's session, or the boat's own address
 * on its Wi-Fi (TOM-855), which has no relay session, still or bitrate.
 */
export interface CameraLiveSession {
  path: 'cloud' | 'lan';
  sessionId: string | null;
  playlistUrl: string;
  stillUrl: string | null;
  expiresAt: string;
  quality: CameraQuality;
  bitrateBps: number | null;
  state: 'starting' | 'streaming';
}

export type CameraLiveReason =
  | 'camera_offline'
  | 'no_such_quality'
  | 'not_configured'
  | 'helper_unavailable'
  | 'upload_unreachable'
  | 'link_paused_starlink_outage'
  | 'token_rejected';

/** GET …/live/status?session= → where the session is. */
export interface CameraLiveStatus {
  phase: 'accepted' | 'streaming' | 'stopped' | 'failed';
  reason?: CameraLiveReason;
  quality: CameraQuality;
  stills: boolean;
}

/** Why a live session could not start, from the POST's refusal. */
export type CameraLiveError =
  | 'boat_offline'
  | 'camera_offline'
  | 'no_such_quality'
  | 'not_found'
  | 'failed';

/** The heading over the event list when the read has not said it yet. */
export const CAMERA_EVENTS_LABEL = 'Seen by Neomaris since connected';

export const CAMERAS_EMPTY =
  'No cameras yet. Cameras appear here once Neomaris on the boat is connected to UniFi Protect.';

export const CAMERA_STARTING = 'Starting…';

/**
 * Said under "Starting…" until the first picture (prod, 2026-10-20: 3 s warm,
 * 8–12 s cold). The apps say the same words.
 */
export const CAMERA_STARTING_NOTE = 'Starting the camera can take up to 15 seconds.';

export function camerasPath(boatId: string): string {
  return `/v1/boats/${encodeURIComponent(boatId)}/cameras`;
}

export function cameraSnapshotUrl(boatId: string, snapshotId: string): string {
  return `${camerasPath(boatId)}/snapshots/${encodeURIComponent(snapshotId)}`;
}

export function cameraLivePath(boatId: string, cameraId: string): string {
  return `${camerasPath(boatId)}/${encodeURIComponent(cameraId)}/live`;
}

/**
 * Night's red treatment for a camera picture, video or still: the shared night
 * tint, laid on once by styles/cameras.css under the night theme only. Set on
 * the media element itself, since the dialogs are portalled out of the page.
 */
export const CAMERA_MEDIA_STYLE = { '--cam-night-tint': NIGHT_TINT } as CSSProperties;

// ----- Alarms ---------------------------------------------------------------

const CAMERA_ALARM_PATH = /^security\.cameras\.[^.]+\.(person|audioAlarm|connected|motion)$/;

/** True for an alarm's data path that is a camera's (`security.cameras.<id>.person`, …). */
export function isCameraAlarmPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && CAMERA_ALARM_PATH.test(path);
}

// ----- Times ----------------------------------------------------------------

function parse(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string') return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** "Oct 19, 09:14", in the viewer's zone, as the alarm history reads. */
export function formatCameraWhen(iso: string, timeZone?: string): string {
  const ms = parse(iso);
  if (ms === null) return iso;
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone === undefined ? {} : { timeZone }),
  });
}

/** "09:14", in the viewer's zone. */
export function formatCameraClock(iso: string, timeZone?: string): string {
  const ms = parse(iso);
  if (ms === null) return iso;
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone === undefined ? {} : { timeZone }),
  });
}

/** "12 min", "1 h 5 min", "under a minute". */
export function formatSpan(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** "Person seen 3 min ago", or null when no person was seen recently. */
export function personSeenLine(camera: Camera, nowMs: number): string | null {
  if (!camera.recentPerson) return null;
  const at = parse(camera.lastPersonAt);
  if (at === null) return 'Person seen recently';
  const minutes = Math.max(0, Math.floor((nowMs - at) / 60_000));
  return minutes < 1 ? 'Person seen just now' : `Person seen ${minutes} min ago`;
}

// ----- Events ---------------------------------------------------------------

const SMART_WORDS: Record<string, string> = {
  vehicle: 'Vehicle',
  animal: 'Animal',
  package: 'Package',
};

const AUDIO_WORDS: Record<string, string> = {
  smoke: 'Smoke alarm sound',
  co: 'CO alarm sound',
  siren: 'Siren sound',
  glass_break: 'Glass breaking sound',
};

/** What one event was, in a few words: "Person", "Camera offline for 12 min". */
export function cameraEventWhat(event: CameraEvent, timeZone?: string): string {
  switch (event.type) {
    case 'person':
      return 'Person';
    case 'smart': {
      const words = event.smartDetectTypes
        .map((t) => SMART_WORDS[t.toLowerCase()])
        .filter((w): w is string => w !== undefined);
      if (words.length === 0) return 'Vehicle, animal or package';
      return words
        .map((w, i) => (i === 0 ? w : w.toLowerCase()))
        .join(words.length === 2 ? ' and ' : ', ');
    }
    case 'audio':
      return (event.audioKind !== null ? AUDIO_WORDS[event.audioKind] : undefined) ?? 'Alarm sound';
    case 'motion':
      return 'Motion';
    case 'offline': {
      const start = parse(event.start);
      const end = parse(event.end);
      if (start === null || end === null) return 'Camera offline';
      return `Camera offline for ${formatSpan(end - start)}`;
    }
    case 'gap': {
      const from = formatCameraClock(event.start, timeZone);
      if (event.end === null) return `Neomaris was not connected since ${from}`;
      return `Neomaris was not connected ${from}–${formatCameraClock(event.end, timeZone)}`;
    }
    default:
      return 'Camera event';
  }
}

/** A gap shorter than this is left out of the event list (Mike, 2026-10-19). */
export const CAMERA_GAP_MIN_MS = 60_000;

/** The events the list shows: a closed gap under a minute is left out. */
export function shownCameraEvents(events: readonly CameraEvent[]): CameraEvent[] {
  return events.filter((e) => {
    if (e.type !== 'gap' || e.end === null) return true;
    const start = Date.parse(e.start);
    const end = Date.parse(e.end);
    return !Number.isFinite(start) || !Number.isFinite(end) || end - start >= CAMERA_GAP_MIN_MS;
  });
}

/** True for a camera that shares a live stream: only that one opens a player. */
export function cameraHasStream(camera: Camera): boolean {
  return camera.streams.length > 0;
}

/** A camera that shares no stream says so on its tile and its panel. */
export const CAMERA_NO_STREAM = 'No shared stream';

/** The event list's type chips; a gap row is always shown, so it has none. */
export const CAMERA_EVENT_FILTERS: readonly { type: CameraEventType; label: string }[] = [
  { type: 'person', label: 'Person' },
  { type: 'smart', label: 'Vehicle, animal, package' },
  { type: 'audio', label: 'Sound' },
  { type: 'motion', label: 'Motion' },
  { type: 'offline', label: 'Offline' },
];

export interface CameraEventFilter {
  cameraId: string | null;
  types: readonly CameraEventType[];
}

/** The events read's query for a filter, a page cursor and a page size. */
export function cameraEventsQuery(
  filter: CameraEventFilter,
  before: string | null,
  limit = 50
): string {
  const q = new URLSearchParams();
  if (filter.cameraId !== null) q.set('cameraId', filter.cameraId);
  // A chosen type keeps the "Neomaris was not connected" spans beside it: the
  // list never hides that it heard nothing for a while.
  if (filter.types.length > 0) {
    const types = CAMERA_EVENT_FILTERS.map((f) => f.type).filter((t) => filter.types.includes(t));
    q.set('type', [...types, 'gap'].join(','));
  }
  if (before !== null) q.set('before', before);
  q.set('limit', String(limit));
  return q.toString();
}

// ----- Live -----------------------------------------------------------------

/** The qualities this camera shares, best first. */
export function cameraQualities(camera: Camera): CameraQuality[] {
  return CAMERA_QUALITIES.filter((q) => camera.streams.some((s) => s.quality === q));
}

/** The lowest stream the camera shares, for the Cameras page's live grid; null with none. */
export function lowestCameraQuality(camera: Camera): CameraQuality | null {
  return cameraQualities(camera).at(-1) ?? null;
}

/**
 * The live grid's whole boat upload: each online camera's lowest stream, once.
 * Null while any of those bitrates is unknown, so no note is shown.
 */
export function gridUploadBps(cameras: readonly Camera[]): number | null {
  let total = 0;
  let any = false;
  for (const c of cameras) {
    const q = lowestCameraQuality(c);
    if (!c.online || q === null) continue;
    const bps = c.streams.find((s) => s.quality === q)?.bitrateBps;
    if (typeof bps !== 'number' || !Number.isFinite(bps) || bps <= 0) return null;
    total += bps;
    any = true;
  }
  return any ? total : null;
}

/** Medium where the camera shares it, else its first; null with no stream. */
export function defaultCameraQuality(camera: Camera): CameraQuality | null {
  const qualities = cameraQualities(camera);
  if (qualities.includes('medium')) return 'medium';
  return qualities[0] ?? null;
}

export function qualityLabel(q: CameraQuality): string {
  return q === 'high' ? 'High' : q === 'medium' ? 'Medium' : 'Low';
}

/** "Uses about 1.0 Mbit/s of boat upload", or null without a bitrate. */
export function uploadNote(bitrateBps: number | null | undefined): string | null {
  if (typeof bitrateBps !== 'number' || !Number.isFinite(bitrateBps) || bitrateBps <= 0) {
    return null;
  }
  const mbit = bitrateBps / 1_000_000;
  const shown = mbit < 0.1 ? '0.1' : mbit.toFixed(1);
  return `Uses about ${shown} Mbit/s of boat upload`;
}

/**
 * Why medium is the default, for a camera that shares more than one stream.
 * Gallivant's high streams send a picture-start every 5 s and medium every 2 s,
 * so medium shows its first picture sooner and loses less when the boat's
 * link drops a stretch (Mike, 2026-10-20: the high streams' setting stays).
 */
export function qualityHint(qualities: readonly CameraQuality[]): string | null {
  return qualities.length > 1 && qualities.includes('medium')
    ? 'Medium starts sooner and loses less video when the boat’s link is weak.'
    : null;
}

/** The POST's refusal, as the player names it. */
export function cameraLiveError(err: unknown): CameraLiveError {
  const e = err as { status?: number; body?: { error?: unknown } } | null;
  const code = typeof e?.body?.error === 'string' ? e.body.error : null;
  if (code === 'boat_offline' || code === 'camera_offline' || code === 'no_such_quality') {
    return code;
  }
  if (e?.status === 404) return 'not_found';
  return 'failed';
}

export type CameraLiveTone = 'good' | 'info' | 'warn' | 'bad';

export interface CameraLiveLine {
  text: string;
  tone: CameraLiveTone;
  /** A second, smaller line: what to expect. */
  note?: string;
}

export interface CameraLiveLineInput {
  error: CameraLiveError | null;
  status: CameraLiveStatus | null;
  /** The video is showing pictures now. */
  playing: boolean;
  /** It showed pictures, then stopped to wait for more. */
  stalled: boolean;
}

const FAILED_WORDS: Record<CameraLiveReason, string> = {
  camera_offline: 'Camera offline',
  no_such_quality: 'This camera doesn’t share that quality',
  not_configured: 'Live video isn’t set up on the boat yet',
  helper_unavailable: 'The boat’s video helper isn’t running',
  upload_unreachable: 'The boat can’t reach the cloud for video',
  link_paused_starlink_outage: 'Starlink outage — video paused',
  token_rejected: 'The boat’s video link was refused',
};

/** The player's one state line. */
export function cameraLiveLine(input: CameraLiveLineInput): CameraLiveLine {
  const { error, status, playing, stalled } = input;
  if (error !== null) {
    switch (error) {
      case 'boat_offline':
        return { text: 'Boat offline', tone: 'bad' };
      case 'camera_offline':
        return { text: 'Camera offline', tone: 'bad' };
      case 'no_such_quality':
        return { text: FAILED_WORDS.no_such_quality, tone: 'warn' };
      case 'not_found':
        return { text: 'This camera isn’t on the boat any more', tone: 'bad' };
      default:
        return { text: 'Couldn’t start the video. Try again.', tone: 'bad' };
    }
  }
  if (status?.reason === 'link_paused_starlink_outage') {
    return { text: FAILED_WORDS.link_paused_starlink_outage, tone: 'warn' };
  }
  if (status?.phase === 'failed') {
    return {
      text: status.reason !== undefined ? FAILED_WORDS[status.reason] : 'The video stopped',
      tone: 'bad',
    };
  }
  if (status?.phase === 'stopped') return { text: 'The video stopped', tone: 'warn' };
  if (status?.stills === true) return { text: 'Weak connection, showing stills', tone: 'warn' };
  if (playing && !stalled) return { text: 'Live', tone: 'good' };
  if (stalled) return { text: 'Reconnecting…', tone: 'warn' };
  return { text: CAMERA_STARTING, tone: 'info', note: CAMERA_STARTING_NOTE };
}

/** Whether the player should show the still in place of the video. */
export function cameraShowsStills(status: CameraLiveStatus | null): boolean {
  return status?.stills === true && status.phase !== 'failed' && status.phase !== 'stopped';
}

/** Whether the session can carry pictures at all (not ended, not refused). */
export function cameraLiveEnded(
  error: CameraLiveError | null,
  status: CameraLiveStatus | null
): boolean {
  return (
    error !== null ||
    status?.phase === 'failed' ||
    status?.phase === 'stopped' ||
    status?.reason === 'link_paused_starlink_outage'
  );
}

/** When to renew a session: about every 2 min, and always before it expires. */
export function cameraRenewDelayMs(expiresAt: string, nowMs: number): number {
  const exp = parse(expiresAt);
  const untilExpiry = exp === null ? 120_000 : exp - nowMs - 20_000;
  return Math.max(5_000, Math.min(120_000, untilExpiry));
}

// "Take a picture now" (TOM-838): the viewer asks the boat for a fresh picture
// and waits for it.
//
//   POST …/cameras/:cameraId/snapshot-requests             → a request (202)
//   GET  …/cameras/:cameraId/snapshot-requests/:requestId  → how it is going

export type SnapshotRequestFailure =
  | 'invalid_command'
  | 'not_configured'
  | 'camera_offline'
  | 'rate_limited'
  | 'too_large'
  | 'unreachable'
  | 'timed_out'
  | 'not_received';

export interface SnapshotRequestView {
  requestId: string;
  cameraId: string;
  requestedAt: string;
  state: 'pending' | 'done' | 'failed';
  snapshot: CameraSnapshotRef | null;
  reason: SnapshotRequestFailure | null;
  retryAfterMs: number | null;
}

/** Why a picture was not taken: the boat's reasons, the cloud's, and the page's own. */
export type TakePictureFailure =
  | SnapshotRequestFailure
  | 'boat_offline'
  | 'needs_core_update'
  | 'not_found'
  | 'failed';

export type TakePictureState =
  | { phase: 'idle' }
  | { phase: 'waiting'; requestId: string | null }
  | { phase: 'done'; snapshot: CameraSnapshotRef }
  | { phase: 'failed'; reason: TakePictureFailure; retryAfterMs: number | null };

export const CAMERA_TAKE_PICTURE = 'Take a picture now';
export const CAMERA_TAKING_PICTURE = 'Taking a picture…';

export function snapshotRequestsPath(boatId: string, cameraId: string): string {
  return `/v1/boats/${boatId}/cameras/${encodeURIComponent(cameraId)}/snapshot-requests`;
}

/** Whether the boat's core can take a picture on request. */
export function canTakePicture(data: CamerasResponse | null | undefined): boolean {
  return data?.snapshotRequests?.available === true;
}

/** The POST's refusal, as the button names it. */
export function takePictureError(err: unknown): {
  reason: TakePictureFailure;
  retryAfterMs: number | null;
} {
  const e = err as { status?: number; body?: { error?: unknown; retryAfterMs?: unknown } } | null;
  const code = typeof e?.body?.error === 'string' ? e.body.error : null;
  const retry = typeof e?.body?.retryAfterMs === 'number' ? e.body.retryAfterMs : null;
  if (
    code === 'boat_offline' ||
    code === 'camera_offline' ||
    code === 'needs_core_update' ||
    code === 'rate_limited'
  ) {
    return { reason: code, retryAfterMs: retry };
  }
  if (e?.status === 404) return { reason: 'not_found', retryAfterMs: null };
  return { reason: 'failed', retryAfterMs: null };
}

const TAKE_PICTURE_WORDS: Record<TakePictureFailure, string> = {
  camera_offline: 'The camera is offline, so it can’t take a picture.',
  not_configured: 'The boat isn’t connected to the camera system.',
  rate_limited: 'Too many pictures just now.',
  too_large: 'The picture was too big for the boat to send.',
  unreachable: 'The boat couldn’t reach the camera.',
  invalid_command: 'The boat couldn’t take that picture.',
  timed_out: 'The boat didn’t answer in time.',
  not_received: 'The picture didn’t arrive.',
  boat_offline: 'The boat is offline.',
  needs_core_update: 'Taking a picture needs a newer Neomaris Core on the boat.',
  not_found: 'This camera isn’t on the boat any more.',
  failed: 'Couldn’t ask the boat for a picture.',
};

/** The line under the button: what happened, and when to try again. */
export function takePictureLine(state: TakePictureState, timeZone?: string): string | null {
  if (state.phase === 'done') {
    return `New picture taken at ${formatCameraClock(state.snapshot.takenAt, timeZone)}.`;
  }
  if (state.phase === 'waiting') return 'Asking the boat. It can take up to 30 seconds.';
  if (state.phase !== 'failed') return null;
  const words = TAKE_PICTURE_WORDS[state.reason];
  if (state.reason === 'rate_limited' && state.retryAfterMs !== null) {
    const s = Math.max(1, Math.ceil(state.retryAfterMs / 1000));
    return `${words} Try again in ${s} second${s === 1 ? '' : 's'}.`;
  }
  const final =
    state.reason === 'camera_offline' ||
    state.reason === 'not_configured' ||
    state.reason === 'needs_core_update' ||
    state.reason === 'not_found';
  return final ? words : `${words} Try again.`;
}

// Pan, tilt and zoom (TOM-831): an owner moves a camera to a preset, or starts
// or stops its patrol, from the big player. One person at a time.
//
//   POST   …/cameras/:cameraId/ptz {action, slot?}     → a command (202)
//   GET    …/cameras/:cameraId/ptz/commands/:commandId → how it went
//   GET    …/cameras/:cameraId/ptz                     → { holder }
//   DELETE …/cameras/:cameraId/ptz                     → let go

export type PtzAction = 'goto_preset' | 'patrol_start' | 'patrol_stop';

export interface PtzHolder {
  name: string;
  you: boolean;
  expiresAt: string;
}

export type PtzFailure =
  | 'invalid_command'
  | 'not_configured'
  | 'camera_offline'
  | 'not_ptz'
  | 'no_such_slot'
  | 'forbidden'
  | 'rate_limited'
  | 'unreachable'
  | 'timed_out'
  | 'boat_offline'
  | 'needs_core_update'
  | 'held_by_other'
  | 'cooldown'
  | 'not_found'
  | 'failed';

export interface PtzCommandView {
  commandId: string;
  cameraId: string;
  action: PtzAction;
  slot: number | null;
  requestedAt: string;
  state: 'pending' | 'ok' | 'failed';
  reason: PtzFailure | null;
  retryAfterMs: number | null;
}

/** Home on every pan, tilt and zoom camera; a preset with no list is tried by number. */
export const PTZ_HOME_SLOT = -1;
/** Presets offered by number when the boat could not read the camera's list. */
export const PTZ_GUESS_PRESETS = 4;

export function cameraPtzPath(boatId: string, cameraId: string): string {
  return `/v1/boats/${boatId}/cameras/${encodeURIComponent(cameraId)}/ptz`;
}

/** Whether the player offers the camera's moves: a pan, tilt and zoom camera on a core that moves it. */
export function canMoveCamera(data: CamerasResponse | null | undefined, camera: Camera): boolean {
  return (
    data?.ptz?.available === true && (camera.ptz !== undefined || /ptz/i.test(camera.model ?? ''))
  );
}

/** The presets to offer, Home first: the camera's own list, or numbers to try. */
export function ptzPresets(camera: Camera): CameraPtzSlot[] {
  const listed = camera.ptz?.presets;
  const rest =
    listed !== null && listed !== undefined
      ? listed.filter((p) => p.slot !== PTZ_HOME_SLOT)
      : Array.from({ length: PTZ_GUESS_PRESETS }, (_, i) => ({ slot: i, name: `Preset ${i + 1}` }));
  return [{ slot: PTZ_HOME_SLOT, name: 'Home' }, ...rest];
}

/** The patrols to offer; none when the boat could not read the list. */
export function ptzPatrols(camera: Camera): CameraPtzSlot[] {
  return camera.ptz?.patrols ?? [];
}

const PTZ_WORDS: Record<PtzFailure, string> = {
  invalid_command: 'The boat couldn’t do that.',
  not_configured: 'The boat isn’t connected to the camera system.',
  camera_offline: 'The camera is offline.',
  not_ptz: 'This camera can’t be moved.',
  no_such_slot: 'That preset isn’t set up on this camera.',
  forbidden: 'The camera system didn’t allow it. Check the Protect key’s permissions.',
  rate_limited: 'The camera is still moving. Try again in a moment.',
  cooldown: 'The camera is still moving. Try again in a moment.',
  unreachable: 'The boat couldn’t reach the camera.',
  timed_out: 'The boat didn’t answer in time.',
  boat_offline: 'The boat is offline.',
  needs_core_update: 'Moving the camera needs a newer Neomaris Core on the boat.',
  held_by_other: 'Someone else is moving this camera.',
  not_found: 'This camera isn’t on the boat any more.',
  failed: 'Couldn’t move the camera.',
};

/** What a move is, in words: "Home", "Cockpit", "patrol Deck sweep". */
function ptzTarget(action: PtzAction, slot: number | null, camera: Camera): string {
  const find = (list: CameraPtzSlot[]) => list.find((x) => x.slot === slot)?.name;
  if (action === 'goto_preset') return find(ptzPresets(camera)) ?? `Preset ${(slot ?? 0) + 1}`;
  return find(ptzPatrols(camera)) ?? `patrol ${(slot ?? 0) + 1}`;
}

export type PtzState =
  | { phase: 'idle' }
  | { phase: 'sending'; action: PtzAction; slot: number | null }
  | { phase: 'ok'; action: PtzAction; slot: number | null }
  | { phase: 'failed'; action: PtzAction; slot: number | null; reason: PtzFailure };

/** The controls' one line, or null. */
export function ptzLine(state: PtzState, camera: Camera, holder: PtzHolder | null): string | null {
  if (holder !== null && !holder.you) return `${holder.name} is moving this camera.`;
  if (state.phase === 'idle') return null;
  const target = ptzTarget(state.action, state.slot, camera);
  if (state.phase === 'sending') {
    if (state.action === 'patrol_stop') return 'Stopping the patrol…';
    return state.action === 'patrol_start' ? `Starting ${target}…` : `Moving to ${target}…`;
  }
  if (state.phase === 'ok') {
    if (state.action === 'patrol_stop') return 'Patrol stopped.';
    return state.action === 'patrol_start' ? `Started ${target}.` : `Moving to ${target}.`;
  }
  if (state.reason === 'no_such_slot' && state.action === 'patrol_start') {
    return 'That patrol isn’t set up on this camera.';
  }
  return PTZ_WORDS[state.reason];
}

/** A refused POST, as the controls name it. */
export function ptzError(err: unknown): { reason: PtzFailure; holder: PtzHolder | null } {
  const e = err as { status?: number; body?: { error?: unknown; holder?: unknown } } | null;
  const code = typeof e?.body?.error === 'string' ? e.body.error : null;
  const holder = (e?.body?.holder ?? null) as PtzHolder | null;
  if (code !== null && code in PTZ_WORDS) return { reason: code as PtzFailure, holder };
  if (e?.status === 404) return { reason: 'not_found', holder: null };
  return { reason: 'failed', holder: null };
}
